import React from 'react';
import Label from 'components/form/Label/Label';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Button } from 'components/form/Button/index';
import SSNField from 'components/Fragments/forms/SSNField/SSNField';
import { ReactComponent as Close } from 'style/icons/close.svg';
import { ReactComponent as CheckIcon } from 'style/icons/icon-checkmark-1.svg';
import './TerminateAccountModal.scss';
import { Link } from 'components/navigation/Link/Link';
import { Notification } from 'components/Notification/Notification';
import { withConfig } from 'hoc/config.hoc.js';
import { useViewModalWatcher } from 'hook/helper.hook';
import { TERMINATE_PREMIUM_USER_MODAL } from 'types/morph.pagePosition.constants';
import { ACCOUNT_SETTINGS } from 'types/mrph.pageName.constants';
import { SmallModal } from 'components/Modals/SmallModal';

const TerminateAccountFooter = ({
  datatestid,
  handleTerminateAccount,
  closeTerminateAccountModal,
  disableTerminateButton,
}) => {
  return (
    <FlexContainer className="justify-end items-align-center">
      <Label
        datatestid={`${datatestid}-cancel`}
        onClick={closeTerminateAccountModal}
        className="link"
      >
        Cancel
      </Label>
      <div className="padR12"></div>
      <Button
        datatestid={`${datatestid}-terminate`}
        mrphType={'destructive'}
        className={'neverFullWidth'}
        onClick={handleTerminateAccount}
        disabled={disableTerminateButton}
      >
        Terminate Account
      </Button>
    </FlexContainer>
  );
};

const TerminateAccountHeader = ({ closeTerminateAccountModal }) => {
  return (
    <FlexContainer className="justify-end">
      <Close className="link" onClick={closeTerminateAccountModal} />
    </FlexContainer>
  );
};

const DeactivatePremiumOrKeepPlan = ({
  datatestid,
  closeTerminateAccountModal,
  showTerminateAccountContactSupport,
  handleShowTerminateAccountContactSupport,
  handleShowManageMyPremiumPlan,
  planName,
  changeToTULabels,
}) => {
  const planLabel = changeToTULabels ? 'Subscription' : planName;
  const accountName = changeToTULabels ? 'TransUnion' : 'Credit Sesame';
  return (
    <FlexContainer className="flex-row">
      <FlexContainer className="padB10 justify-start">
        <Label
          datatestid={`${datatestid}-terminateTitle`}
          type="H4 mH5"
          className="m-text-center justify-start"
        >
          Are you looking to deactivate {planLabel}?
        </Label>
      </FlexContainer>

      <Label
        datatestid={`${datatestid}-premiumSupportText`}
        className="padTB10"
      >
        Deactivating {planLabel} allows you to stay a free member and keep all
        your {accountName} account data.
      </Label>
      <div className="mobileHide">
        <Button
          datatestid={`${datatestid}-terminate`}
          className="t-width100"
          mrphType="destructiveLink"
          onClick={handleShowTerminateAccountContactSupport}
        >
          No, I want to permanently delete my account
        </Button>
        <Button
          datatestid={`${datatestid}-keepAccount`}
          className="t-width100"
          mrphType="primary"
          onClick={handleShowManageMyPremiumPlan}
        >
          Manage my {planLabel} Plan
        </Button>
      </div>
      <div className="nonMobileHide">
        <Button
          datatestid={`${datatestid}-keepAccount`}
          className="t-width100"
          mrphType="primary"
          onClick={handleShowManageMyPremiumPlan}
        >
          Manage my {planLabel} Plan
        </Button>
        <Button
          datatestid={`${datatestid}-terminate`}
          className="t-width100"
          mrphType="destructiveLink"
          onClick={handleShowTerminateAccountContactSupport}
        >
          No, I want to permanently delete my account
        </Button>
      </div>
    </FlexContainer>
  );
};

const TerminateAccountContactSupport = ({
  datatestid,
  sesameCashEnrolledView,
  isPremiumUser,
  cashSupportLink,
  premiumSupportLink,
  handleTerminateAccountSupportClick,
  changeToTULabels,
}) => {
  const accountName = changeToTULabels ? 'TransUnion' : 'Credit Sesame';
  const showPremiumSupport =
    isPremiumUser && !sesameCashEnrolledView ? true : false;
  return (
    <FlexContainer className="flex-row">
      <Label
        datatestid={`${datatestid}-terminateTitle`}
        type="H4 mH5"
        className="m-text-center"
      >
        Account Termination
      </Label>
      <div className="padB16"></div>
      {showPremiumSupport ? (
        <>
          <Label
            datatestid={`${datatestid}-premiumSupportText`}
            className="padB10"
          >
            Please contact Customer Support to permanently terminate your{' '}
            {accountName} account.
          </Label>
          <FlexContainer className="width100 justify-end">
            <Button
              datatestid={`${datatestid}-terminate`}
              className="t-width100"
              mrphType="primary"
              onClick={() =>
                handleTerminateAccountSupportClick(premiumSupportLink)
              }
            >
              Contact Support
            </Button>
          </FlexContainer>
        </>
      ) : (
        <Label datatestid={`${datatestid}-cashSupportText`}>
          Please contact Sesame Cash support at{' '}
          <Link
            onClickCallback={() => {
              handleTerminateAccountSupportClick(cashSupportLink);
            }}
            to="/profile"
          >
            support.sesamecash.com
          </Link>{' '}
          to terminate your Credit Sesame account.
        </Label>
      )}
    </FlexContainer>
  );
};

const TerminateAccountSSNValidation = ({
  datatestid,
  ssn,
  setSsn,
  terminateAccountError,
  terminateAccountDescription,
}) => {
  return (
    <FlexContainer className="flex-column">
      {terminateAccountError && (
        <FlexContainer className="flex-column">
          <Notification
            datatestid={`${datatestid}-ssnError`}
            style={{}}
            type="error"
            id={1}
            duration={5000}
            transient={true}
            title={<span>{terminateAccountError.message}</span>}
            open={true}
            notClosable={true}
          />
          <div className="padB16 "></div>
        </FlexContainer>
      )}
      <Label datatestid={`${datatestid}-ssnText`} type="header-description">
        {terminateAccountDescription}
      </Label>
      <div className="padB24 mobileHide"></div>
      <div className="padB16 desktopHide"></div>
      <SSNField
        datatestid={datatestid}
        ssn={ssn}
        setSsn={setSsn}
        fullSsn={false}
        requiredSsnCharaters={4}
        placeholder={'Last 4 digits of SSN'}
        autofocus={true}
        error={!!ssn.error}
      />
    </FlexContainer>
  );
};

export const TerminateAccountModalBody = ({
  datatestid,
  showSupportMessage,
  sesameCashEnrolledView,
  isPremiumUser,
  cashSupportLink,
  premiumSupportLink,
  handleTerminateAccountSupportClick,
  ssn,
  setSsn,
  terminateAccountError,
  terminateAccountDescription,
  closeTerminateAccountModal,
  showTerminateAccountContactSupport,
  handleShowTerminateAccountContactSupport,
  showDeactivatePremium,
  handleShowManageMyPremiumPlan,
  planName,
  changeToTULabels,
}) => {
  return (
    <FlexContainer className="flex-column terminateModalBody ">
      {showSupportMessage ? (
        <>
          {!showTerminateAccountContactSupport ? (
            <DeactivatePremiumOrKeepPlan
              datatestid={datatestid}
              isPremiumUser={isPremiumUser}
              sesameCashEnrolledView={sesameCashEnrolledView}
              cashSupportLink={cashSupportLink}
              premiumSupportLink={premiumSupportLink}
              handleTerminateAccountSupportClick={
                handleTerminateAccountSupportClick
              }
              closeTerminateAccountModal={closeTerminateAccountModal}
              showTerminateAccountContactSupport={
                showTerminateAccountContactSupport
              }
              handleShowTerminateAccountContactSupport={
                handleShowTerminateAccountContactSupport
              }
              handleShowManageMyPremiumPlan={handleShowManageMyPremiumPlan}
              showDeactivatePremium={showDeactivatePremium}
              planName={planName}
              changeToTULabels={changeToTULabels}
            />
          ) : (
            <TerminateAccountContactSupport
              datatestid={datatestid}
              isPremiumUser={isPremiumUser}
              sesameCashEnrolledView={sesameCashEnrolledView}
              cashSupportLink={cashSupportLink}
              premiumSupportLink={premiumSupportLink}
              handleTerminateAccountSupportClick={
                handleTerminateAccountSupportClick
              }
              handleShowManageMyPremiumPlan={handleShowManageMyPremiumPlan}
              showDeactivatePremium={showDeactivatePremium}
              planName={planName}
              changeToTULabels={changeToTULabels}
            />
          )}
        </>
      ) : (
        <TerminateAccountSSNValidation
          datatestid={datatestid}
          ssn={ssn}
          setSsn={setSsn}
          terminateAccountError={terminateAccountError}
          terminateAccountDescription={terminateAccountDescription}
        />
      )}
    </FlexContainer>
  );
};

const TerminateAccountModal = ({
  datatestid,
  showTerminateAccountModal,
  handleTerminateAccount,
  handleTerminateAccountModal,
  sesameCashEnrolledView,
  isPremiumUser,
  showTerminateAccountContactSupport,
  ssn,
  ...props
}) => {
  useViewModalWatcher(
    {
      'Pop Up Name': TERMINATE_PREMIUM_USER_MODAL,
      'Page Position': TERMINATE_PREMIUM_USER_MODAL,
      'Page Name': ACCOUNT_SETTINGS,
    },
    showTerminateAccountModal,
  );
  const showSupportMessage = isPremiumUser || sesameCashEnrolledView;
  const disableTerminateButton = ssn.label.length < 4;
  let premiumSupportLink = '';
  let cashSupportLink = '';
  let terminateAccountDescription =
    'By terminating your account, you will lose all of your account data, and you will have to sign up again to receive free credit insights and monitoring alerts. Confirm by typing the last 4 digits of your social security number (SSN)';

  if (props.config) {
    premiumSupportLink =
      props.config.supportLinks.terminateAccountPremiumSupportLink;
    cashSupportLink = props.config.supportLinks.terminateAccountCashSupportLink;
    terminateAccountDescription =
      props.config.disclaimers.terminateAccount.terminateAccountSSNText;
  }

  const terminalAccountFooter = showSupportMessage ? null : (
    <TerminateAccountFooter
      datatestid={datatestid}
      handleTerminateAccount={handleTerminateAccount}
      showSupportMessage={showSupportMessage}
      closeTerminateAccountModal={handleTerminateAccountModal}
      disableTerminateButton={disableTerminateButton}
    />
  );
  return (
    <SmallModal
      visible={showTerminateAccountModal}
      closable={false}
      onCancel={handleTerminateAccountModal}
      footer={terminalAccountFooter}
      title={
        <TerminateAccountHeader
          datatestid={datatestid}
          closeTerminateAccountModal={handleTerminateAccountModal}
        />
      }
      wrapClassName={'TerminateAccountModal'}
    >
      <TerminateAccountModalBody
        datatestid={datatestid}
        showSupportMessage={showSupportMessage}
        isPremiumUser={isPremiumUser}
        sesameCashEnrolledView={sesameCashEnrolledView}
        cashSupportLink={cashSupportLink}
        premiumSupportLink={premiumSupportLink}
        ssn={ssn}
        terminateAccountDescription={terminateAccountDescription}
        showTerminateAccountModal={showTerminateAccountModal}
        closeTerminateAccountModal={handleTerminateAccountModal}
        showTerminateAccountContactSupport={showTerminateAccountContactSupport}
        {...props}
      />
    </SmallModal>
  );
};

const withConfigTerminateAccountModal = withConfig(TerminateAccountModal);

const AccountSuccessfullyTerminatedModal = ({
  datatestid,
  showAccountSuccessfullyTerminatedModal,
  handleAccountSuccessfullyTerminated,
}) => {
  return (
    <SmallModal
      className="AccountSuccessfullyTerminatedModal"
      visible={showAccountSuccessfullyTerminatedModal}
      onCancel={handleAccountSuccessfullyTerminated}
      noCloseButton
      width="448px"
      footer={
        <Button
          datatestid={`${datatestid}-successfullyTerminated`}
          mrphType="primary"
          onClick={handleAccountSuccessfullyTerminated}
        >
          Close
        </Button>
      }
    >
      <FlexContainer className="flex-column items-align-center content-align-center">
        <CheckIcon className="margT40" />
        <Label
          datatestid={`${datatestid}-terminateTitle`}
          className="margT16 text-20 bold text-center"
        >
          Your account has been successfully terminated
        </Label>
        <Label className="margT8 text-16">You will now be logged out.</Label>
      </FlexContainer>
    </SmallModal>
  );
};

const withConfigAccountSuccessfullyTerminatedModal = withConfig(
  AccountSuccessfullyTerminatedModal,
);

export {
  withConfigTerminateAccountModal as TerminateAccountModal,
  withConfigAccountSuccessfullyTerminatedModal as AccountSuccessfullyTerminatedModal,
};
